import { useEffect, useState } from 'react';
import { useUserClientContext } from '@/app/clientContext';
import { getAppConfig } from '@/app/config';
import {
  createLDReactProvider,
  type LDInspection,
  useLDClient,
} from '@launchdarkly/react-sdk';
import { buildLaunchDarklyFlagUsedHandler } from '@sentry/react';
import { ldIdentifiedContext } from '@/hooks/useLDIdentified';

const buildFlagUsedInspector = (): Extract<
  LDInspection,
  { type: 'flag-used' }
> => {
  const sentryHandler = buildLaunchDarklyFlagUsedHandler();
  return {
    ...sentryHandler,
    method: (flagKey, flagDetail, context) =>
      sentryHandler.method(flagKey, flagDetail, context ?? {}),
  };
};

export const LaunchDarklyWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { launchDarkly, launchDarklyLocalUri } = getAppConfig();

  const [LDProvider] = useState(() =>
    createLDReactProvider(
      launchDarkly,
      { kind: 'user', key: 'anonymous' },
      {
        startOptions: { timeout: 5 },
        ldOptions: {
          inspectors: [buildFlagUsedInspector()],
          baseUri: launchDarklyLocalUri
            ? launchDarklyLocalUri
            : 'https://clientsdk.launchdarkly.com',
          streamUri: launchDarklyLocalUri
            ? launchDarklyLocalUri
            : 'https://clientstream.launchdarkly.com',
          eventsUri: launchDarklyLocalUri
            ? launchDarklyLocalUri
            : 'https://events.launchdarkly.com',
        },
      }
    )
  );

  return (
    <LDProvider>
      <LDIdentificationGate>{children}</LDIdentificationGate>
    </LDProvider>
  );
};

export const LDIdentificationGate = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { actualLoginEmail, context: clientContext } = useUserClientContext();
  const ldClient = useLDClient();
  const clientCode = clientContext.code;
  const [identified, setIdentified] = useState<{
    key: string;
    clientCode: string;
  } | null>(null);

  useEffect(() => {
    if (!actualLoginEmail || !clientCode) return;

    let cancelled = false;
    ldClient
      .identify({
        kind: 'user',
        key: actualLoginEmail,
        clientCode,
      })
      .then(() => {
        if (!cancelled) setIdentified({ key: actualLoginEmail, clientCode });
      });

    return () => {
      cancelled = true;
    };
  }, [ldClient, actualLoginEmail, clientCode]);

  const isIdentified =
    identified?.key === actualLoginEmail &&
    identified.clientCode === clientCode;

  return (
    <ldIdentifiedContext.Provider value={isIdentified}>
      {children}
    </ldIdentifiedContext.Provider>
  );
};
